import declare from "dojo/_base/declare";
import _TemplatedMixin from "dijit/_TemplatedMixin";
import _WidgetBase from "dijit/_WidgetBase";
import _ from "underscore";
import Heading from "csi/def/Heading";
import csiHeading from "csi/heading/_base";
import html from "csi/html";
import reflection from "csi/reflection";
import xml from "csi/xml";
import template from "!!raw-loader!./AttributeCard.html";

// TODO: Need API to get params for attribute cards, along with necessary dependency paths (csi/store/UserNames?)

const MAX_DETAILED_ATTRIBUTES = 5;

const CSS_SINGLE_LINE = "-singleline";
const CSS_MULTI_LINE = "-multiline";

const CSS_SLIM = "-slim";
const CSS_DETAILED = "-detailed";
const CSS_STACKED = "-stacked";

const DEFAULT_HREF = "javascript:;";

/**
 * Assign minimum width to identifier span to prevent it from collapsing
 * @param {DOMNode} span
 */
const _setMinWidth = (span): void => {
	// 3px offset is to leave space for separator between two components
	const text = span.innerText;
	if (text) {
		const width = Math.ceil(html.getTextWidth(text) + 1);
		span.style.minWidth = `min(${width}px, 50% - 3px)`;
	}
};

const AttributeCard = declare([_WidgetBase, _TemplatedMixin], {
	baseClass: "csi-attribute-card",
	templateString: template,

	// attach points
	anchorNode: null,
	bottomRightNode: null,
	imageNode: null,
	identifierNode: null,
	primaryNode: null,
	repeatNode: null,
	rightNode: null,
	secondaryNode: null,
	separatorNode: null,
	upperRightNode: null,

	/** @property {string} anchorHref - href to use as main anchor for card; use in conjunction with anchorKey */
	anchorHref: DEFAULT_HREF,

	/** @property {string} anchorText - text for anchor */
	anchorText: "",

	/** @property {Spec[]} attributeSpecs - Specs to use to render other data of instance */
	attributeSpecs: null,

	/** @property {Spec} bottomRightSpec - Spec to render in bottom right corner of card */
	bottomRightSpec: null,

	/** @property {Context} ctx - context used to render card Headings */
	ctx: null,

	/** @property {Heading} defaultImageHeading - set to null to prevent default no-image placeholder icon */
	defaultImageHeading: _.extend(new Heading(), {
		m_class: "csi-attribute-card-no-image csiActionIcon material-icons",
		m_value: "image",
	}),

	/** @property {boolean} hideImage - hide placeholder for image */
	hideImage: false,

	/** @property {Spec} imageSpec - Spec to use to render instance image */
	imageSpec: null,

	/** @property {boolean} isTooltip - true if rendering card in a tooltip */
	isTooltip: false,

	/** @property {boolean} multiline - only for slim view: if true, then attributes will not wrap individually
	 * if not multiline mode, then outer widget should call setWrapSizes(attrMaxWidth) and supply maximum allowed attribute width without collapsing */
	multiline: false,

	/** @property {string} pathToInstance - path from node in context to node instance to use for rendering Headings */
	pathToInstance: "",

	/** @property {string} primaryTooltip - primary tooltip only for stacked, slim view */
	primaryTooltip: null,

	/** @property {string} secondaryTooltip - secondary tooltip only for stacked, slim view */
	secondaryTooltip: null,

	/** @property {boolean} slimLabels - show labels for numeric and boolean values, slim view */
	slimLabels: false,

	/** @property {boolean} slim - if true, then render slim card; otherwise, render detailed card (default) */
	slim: false,

	/** @property {boolean} primarySlimLinkStyle - if true, then show links for first attribute in slim mode */
	primarySlimLinkStyle: false,

	/** @property {boolean} slimLink - if true, then show links for attributes in slim mode */
	slimLink: false,

	/** @property {boolean} stacked - only for slim view: if true, then attributes will be stacked vertically and not displayed inline horizontally */
	stacked: false,

	/** @property {Spec} upperRightSpec - Spec to render in upper right corner of card */
	upperRightSpec: null,

	/**
	 * Extension point to respond to clicking on node
	 * @param {Node} anchorNode
	 * @param {Node} instanceNode
	 */
	onClick: null,

	/**
	 * Render data
	 */
	postCreate() {
		const domNode = this.domNode;

		this.inherited(arguments);

		// copy context to prevent context changes affecting main view and mark as read-only
		const ctx = this.ctx.copyContext();
		ctx.bCard = true;
		ctx.setReadOnly(true);

		// determine CSS and render anchor
		const css = [this.baseClass + (this.slim ? CSS_SLIM : CSS_DETAILED)];

		if (this.slim) {
			css.push(this.baseClass + (this.multiline ? CSS_MULTI_LINE : CSS_SINGLE_LINE));
		}

		if (this.stacked) {
			css.push(this.baseClass + CSS_STACKED);
		}

		if (!this.anchorText) {
			html.setVisible(this.anchorNode, false);
		} else if (!this.onClick && (!this.anchorHref || (this.anchorHref === DEFAULT_HREF))) {
			html.setInnerText(this.anchorNode, this.anchorText);
		}

		// render upper right and bottom right elements
		let defs = {} as Record<string, Heading>;

		if (this.upperRightSpec) {
			defs.upperRight = this.upperRightSpec.heading;
		} else {
			$(this.upperRightNode).hide();
		}

		if (this.bottomRightSpec) {
			defs.bottomRight = this.bottomRightSpec.heading;
		} else {
			$(this.bottomRightNode).hide();
		}

		// maintain unused keys in template since it could be called again for instance
		html.templateToHtml(ctx, domNode, defs, null, true, true);

		// render instance information
		const bInstance = ctx.push(this.pathToInstance);
		const node = bInstance ? ctx.getNode() : null;
		const specs = this.attributeSpecs;
		const primarySpec = specs ? specs[0] : null;
		const secondarySpec = (specs && this.slim) ? specs[1] : null;
		const customSpecs = (!specs || this.slim) ? null : specs.slice(1, MAX_DETAILED_ATTRIBUTES);

		defs = {};

		if (node) {
			css.push(this.baseClass + "-" + node.$Type);
		}

		if (this.hideImage) {
			$(this.imageNode).hide();
		} else if (bInstance && this.imageSpec) {
			if (csiHeading.toImageViewable(ctx, this.imageSpec.heading)) {
				defs.image = this.imageSpec.heading;
			} else if (!this.isTooltip) {
				// for card without image we don't show placeholder in tooltip, so heading defs.image = undefined
				defs.image = this.defaultImageHeading;
			}
		}

		if (bInstance && primarySpec) {
			defs.primary = primarySpec.heading;
			defs.primary.m_link = this.slimLink ? "" : null;

			if (secondarySpec) {
				defs.secondary = secondarySpec.heading;
				defs.secondary.m_link = this.slimLink ? "" : null;
				if (this.stacked) {
					$(this.separatorNode).hide();
				}
			} else {
				$(this.separatorNode).hide();
				$(this.secondaryNode).hide();
			}

			if (customSpecs) {
				defs[html.DATA_TEMPLATE_KEY_REPEAT] = _.pluck(customSpecs, "heading");
			} else {
				$(this.repeatNode).hide();
			}
		} else {
			$(this.separatorNode).hide();
		}

		// restrict links in template other than anchor (unless explicitly configured for slim mode), and restrict image size to small (thumbnail)
		if (bInstance) {
			const tempBrowse = ctx.bForceBrowse;

			if (!this.slim || !this.slimLink) {
				ctx.bForceBrowse = false;
				ctx.bReserveClick = true;
			}

			ctx.prefImageSize = reflection.IMAGE_SIZE_SMALL;
			// Create slim labels for boolean and numeric attributes
			if (this.slimLabels) {
				this._createSlimLabel(primarySpec, this.primaryNode);
				this._createSlimLabel(secondarySpec, this.secondaryNode);
			}

			html.templateToHtml(ctx, domNode, defs, null, false, true);
			ctx.bForceBrowse = tempBrowse;

			if (this.primarySlimLinkStyle) {
				$(this.primaryNode).css("color", "var(--LinkNormal)");
			}

			// reset links
			if (this.slimLink) {
				if (defs.primary) {
					defs.primary.m_link = null;
				}

				if (defs.secondary) {
					defs.secondary.m_link = null;
				}
			}
		}

		// if no image in detailed card, then hide node to reduce space
		if (this.isTooltip && html.isEmpty(this.imageNode.firstChild)) {
			$(this.imageNode).hide();
		}

		this._setWrapSizes();

		$(domNode).addClass(css.join(" "));
	},

	/**
	 * Create boolean label at domNode
	 * @param {string} name
	 * @param {Node} domNode
	 */
	_createBooleanLabelNode(name, domNode) {
		html.create("span", {
			class: "dijitInline",
			style: "margin-right: 2px",
			innerText: name + ": ",
		}, domNode);
	},

	/**
	 * FIX: Currently, if the label is too long, the value will be cut off. A couple suggestions I've given to UX include:
	 * 1. Do not show the label if the heading name is too long
	 * 2. Use a custom string attribute that uses an expression to populate itself based on the other attribute’s value.
	 *		For example, rather than show: “Active: X:, the string attribute could just say “Active” or “Inactive”.
		* 3. Show the first / last word only if the heading name is too long (Here is a some code to show only the first word)
		* let primarySpecName; 
		* if (html.getTextWidth(primarySpec.heading.m_name) < constants.ATTRIBUTE_CARD_DEFAULT_WIDTH) {
		* 	primarySpecName = primarySpec.heading.m_name;
		* } else {
		* 	const splitName = primarySpec.heading.m_name.split(" ");
		* 	primarySpecName = splitName[0];
		* }
		* Create slim labels for boolean and numeric attributes
		* @param {Object} attributeSpec - spec
		* @param {Node} domNode - domNode to prepend label to
		*/
	_createSlimLabel(attributeSpec, domNode) {
		if (!attributeSpec) {
			return;
		}
		const { heading, type } = attributeSpec;
		if (type === xml.ATTR_BOOLEAN) {
			this._createBooleanLabelNode(heading.m_name, domNode);
		} else if (xml.attributeTypeIsNumeric(type)) {
			html.text(domNode, heading.m_name + ": ");
		}
	},

	/**
	 * Return node which will be used to display values
	 * @param {Node} anchorNode
	 * @return {Node}
	 */
	_getInstanceNode(anchorNode) {
		return this.pathToInstance ? this.ctx.cache.getPathNode(anchorNode, this.pathToInstance) : anchorNode;
	},

	/**
	 * Respond to click on anchor
	 * @param {Event} evt
	 */
	_onClickAnchor(evt) {
		const anchorNode = this.ctx.getNode();

		if (this.onClick) {
			this.onClick(anchorNode, this._getInstanceNode(anchorNode));
		}
	},

	/**
	 * Set min-width for narrow attribute to prevent it from collapsing
	 */
	_setWrapSizes() {
		const { attributeSpecs, multiline, primaryNode, secondaryNode, stacked } = this;

		if (multiline || stacked || !attributeSpecs || (attributeSpecs.length < 2)) {
			return;
		}

		_setMinWidth(primaryNode);
		_setMinWidth(secondaryNode);
	},
});

export = AttributeCard;